/**
 * Shopify Wishlist Manager
 * Merkliste for logged-in customers: stored in customer.metafields.custom.merkliste via the
 * app proxy (/apps/omr/merkliste), cached in localStorage. Guests get a login dialog instead.
 *
 * SECTIONS:
 * 1. Configuration & Utilities
 * 2. Storage Operations
 * 3. Product Operations
 * 4. Account Sync & Login Gate
 * 5. UI State Updates
 * 6. Toast Notifications
 * 7. Wishlist Page Rendering
 * 8. Initialization
 */

(function () {
  'use strict';

  /* ==========================================================================
     1. CONFIGURATION & UTILITIES
     ========================================================================== */

  // Constants
  const STORAGE_KEY = 'shopify_wishlist';
  const PENDING_KEY = 'shopify_wishlist_pending';
  const PENDING_TTL = 30 * 60 * 1000;
  const MAX_ITEMS = 50;
  const MAX_STORAGE_SIZE = 500000; // 500KB localStorage limit
  const SYNC_URL = '/apps/omr/merkliste';
  const SYNC_DELAY = 1500;

  /**
   * Escape HTML entities to prevent XSS attacks
   * @param {string} str - String to escape
   * @returns {string} Escaped string
   */
  const escapeHtml = (str) => {
    if (typeof str !== 'string') return '';
    const entities = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
    return str.replace(/[&<>"']/g, (c) => entities[c]);
  };

  /**
   * Safely parse JSON with prototype pollution protection
   * @param {string} jsonString - JSON string to parse
   * @returns {any} Parsed object or null on error
   */
  const safeJsonParse = (jsonString) => {
    return JSON.parse(jsonString, (key, value) => {
      // Block prototype pollution attacks
      if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
        return undefined;
      }
      return value;
    });
  };

  /**
   * Validate that URL is a safe product URL (prevents open redirect)
   * @param {string} url - URL to validate
   * @returns {boolean} True if URL is safe
   */
  const isValidProductUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    // Only allow relative /products/ URLs
    // Check for protocol injection (://) but allow encoded slashes (%2F)
    if (url.includes('://') || url.includes('javascript:')) return false;
    return url.startsWith('/products/');
  };

  /**
   * Validate that image URL is from trusted source
   * @param {string} url - Image URL to validate
   * @returns {boolean} True if URL is trusted
   */
  const isValidImageUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    // Allow Shopify CDN and relative URLs only
    return url.includes('cdn.shopify.com') || url.startsWith('/');
  };

  /**
   * Product ids the app proxy accepts (numeric Shopify product ids)
   * @param {Object} item - Wishlist item
   * @returns {boolean} True if the id is numeric
   */
  const hasProductId = (item) => /^\d+$/.test(String(item && item.id));

  /**
   * Ids of a list of wishlist items, as strings
   * @returns {string[]}
   */
  const idsOf = (items) => items.map((item) => String(item.id));

  /**
   * Compare two id lists by value and order
   * @returns {boolean} True if both lists hold the same ids in the same order
   */
  const sameIds = (a, b) => a.length === b.length && a.every((id, i) => id === b[i]);

  /**
   * Read the state rendered by snippets/global-wishlist-state.liquid
   * @returns {{customerId: string|null, loginUrl: string, items: Object[]}}
   */
  const readPageState = () => {
    const fallback = { customerId: null, loginUrl: '/account/login', items: [] };
    const el = document.getElementById('wishlist-state');
    if (!el) return fallback;
    try {
      const state = safeJsonParse(el.textContent);
      return {
        customerId: state.customerId ? String(state.customerId) : null,
        loginUrl: state.loginUrl || fallback.loginUrl,
        items: Array.isArray(state.items) ? state.items.filter(hasProductId) : [],
      };
    } catch (e) {
      console.error('Wishlist: invalid page state', e);
      return fallback;
    }
  };

  /* ==========================================================================
     2. STORAGE OPERATIONS
     ========================================================================== */

  const WishlistManager = {
    pageState: { customerId: null, loginUrl: '/account/login', items: [] },
    syncTimer: null,
    syncInFlight: false,
    syncQueued: false,

    /**
     * Check if localStorage is available
     */
    checkLocalStorage: function () {
      try {
        const test = '__test__';
        localStorage.setItem(test, test);
        localStorage.removeItem(test);
        return true;
      } catch (e) {
        return false;
      }
    },

    /**
     * Initialize storage with empty wishlist if needed
     */
    initializeStorage: function () {
      const data = localStorage.getItem(STORAGE_KEY);
      if (!data) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ items: [] }));
      }
    },

    /**
     * Get wishlist data from localStorage
     * Shape: { items, customerId?, dirty?, baseIds? } – customerId marks a cache owned by an account,
     * dirty marks changes not yet confirmed by the app proxy, baseIds is the account list those
     * changes started from.
     * Uses safe JSON parsing to prevent prototype pollution
     */
    getData: function () {
      try {
        const data = localStorage.getItem(STORAGE_KEY);
        const parsed = data ? safeJsonParse(data) : null;
        return parsed && Array.isArray(parsed.items) ? parsed : { items: [] };
      } catch (e) {
        console.error('Error reading wishlist:', e);
        return { items: [] };
      }
    },

    /**
     * Save wishlist data to localStorage
     * Includes size validation to prevent DoS
     * @returns {boolean} Success status
     */
    saveData: function (data) {
      try {
        const serialized = JSON.stringify(data);

        // Prevent localStorage bloat
        if (serialized.length > MAX_STORAGE_SIZE) {
          console.error('Wishlist data exceeds size limit');
          this.showToast('Daten zu groß');
          return false;
        }

        localStorage.setItem(STORAGE_KEY, serialized);
        return true;
      } catch (e) {
        // Handles QuotaExceededError
        console.error('Error saving wishlist:', e);
        this.showToast('Speicherfehler');
        return false;
      }
    },

    /**
     * Save a changed item list for the logged-in customer and schedule the account sync
     * @returns {boolean} Success status
     */
    commitItems: function (items) {
      const saved = this.saveData({
        items,
        customerId: this.pageState.customerId,
        dirty: true,
        baseIds: this.getData().baseIds,
      });
      if (saved) this.scheduleSync();
      return saved;
    },

    /* ==========================================================================
       3. PRODUCT OPERATIONS
       ========================================================================== */

    /**
     * Whether a customer is logged in (the list lives in the account)
     */
    isLoggedIn: function () {
      return Boolean(this.pageState.customerId);
    },

    /**
     * Add product to wishlist
     * @returns {boolean} Success status
     */
    addProduct: function (product) {
      if (!this.isLoggedIn() || !hasProductId(product)) return false;

      const data = this.getData();

      if (data.items.length >= MAX_ITEMS) {
        this.showToast('Merkliste ist voll');
        return false;
      }

      if (this.isInWishlist(product.id)) {
        return false;
      }

      data.items.push(product);
      this.commitItems(data.items);

      // GTM tracking
      if (window.dataLayer) {
        try {
          pushEventToDataLayer('wishList:addProduct', '', window.location.href, '');
        } catch (e) {
          console.warn('Wishlist GTM tracking (add) failed:', e);
        }
      }

      return true;
    },

    /**
     * Remove product from wishlist
     * @returns {boolean} Success status
     */
    removeProduct: function (productId) {
      if (!this.isLoggedIn()) return false;

      const data = this.getData();
      const normalizedId = String(productId);
      const product = data.items.find((item) => String(item.id) === normalizedId);

      this.commitItems(data.items.filter((item) => String(item.id) !== normalizedId));

      // GTM tracking
      if (window.dataLayer && product) {
        try {
          pushEventToDataLayer('wishList:removeProduct', '', window.location.href, '');
        } catch (e) {
          console.warn('Wishlist GTM tracking (remove) failed:', e);
        }
      }

      return true;
    },

    /**
     * Check if product is in wishlist
     * Normalizes IDs to strings to prevent type coercion issues
     */
    isInWishlist: function (productId) {
      const normalizedId = String(productId);
      return this.getWishlist().some((item) => String(item.id) === normalizedId);
    },

    /**
     * Get all wishlist items (always empty for guests)
     */
    getWishlist: function () {
      return this.isLoggedIn() ? this.getData().items : [];
    },

    /**
     * Get wishlist item count
     */
    getWishlistCount: function () {
      return this.getWishlist().length;
    },

    /**
     * Clear all items from wishlist
     */
    clearWishlist: function () {
      if (!this.isLoggedIn()) return false;
      this.commitItems([]);
      return true;
    },

    /* ==========================================================================
       4. ACCOUNT SYNC & LOGIN GATE
       ========================================================================== */

    /**
     * Align the localStorage cache with the page state on page load.
     * Guests: an account-owned cache is dropped; an owner-less guest list is kept for the first login.
     * Logged in: unsynced own changes are resent only if the account list is still the one they
     * started from (otherwise another device changed it and the account list wins); an owner-less
     * guest list is merged once; otherwise the account list from Liquid replaces the cache.
     */
    reconcileWithAccount: function () {
      const data = this.getData();
      const customerId = this.pageState.customerId;

      if (!customerId) {
        if (data.customerId) this.saveData({ items: [] });
        return;
      }

      const accountItems = this.pageState.items;
      const accountIds = idsOf(accountItems);

      if (data.customerId === customerId && data.dirty) {
        if (sameIds(idsOf(data.items), accountIds)) {
          this.saveData({ items: data.items, customerId, dirty: false, baseIds: accountIds });
          return;
        }
        if (Array.isArray(data.baseIds) && sameIds(data.baseIds, accountIds)) {
          this.sync();
          return;
        }
      }

      if (!data.customerId && data.items.length > 0) {
        const merged = accountItems.slice();
        data.items.filter(hasProductId).forEach((item) => {
          const known = merged.some((m) => String(m.id) === String(item.id));
          if (!known && merged.length < MAX_ITEMS) merged.push(item);
        });
        const changed = !sameIds(idsOf(merged), accountIds);
        this.saveData({ items: merged, customerId, dirty: changed, baseIds: accountIds });
        if (changed) this.sync();
        return;
      }

      this.saveData({ items: accountItems, customerId, dirty: false, baseIds: accountIds });
    },

    /**
     * Debounce the account sync so quick toggles end up in one request
     */
    scheduleSync: function () {
      clearTimeout(this.syncTimer);
      this.syncTimer = setTimeout(() => {
        this.syncTimer = null;
        this.sync();
      }, SYNC_DELAY);
    },

    /**
     * Send the complete list to the app proxy; the response is the stored list.
     * One request at a time; a response is only applied if the list did not change meanwhile.
     * @param {boolean} [keepalive] - Use a keepalive request (page is being left)
     */
    sync: async function (keepalive) {
      if (!this.isLoggedIn()) return;
      if (this.syncInFlight) {
        this.syncQueued = true;
        return;
      }

      const sent = this.getData();
      if (!sent.dirty) return;

      this.syncInFlight = true;
      try {
        const response = await fetch(SYNC_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ products: sent.items.filter(hasProductId).map((item) => Number(item.id)) }),
          credentials: 'same-origin',
          keepalive: Boolean(keepalive),
        });
        const body = await response.json().catch(() => null);
        if (!response.ok || !body || !Array.isArray(body.products)) {
          throw new Error(`Wishlist sync failed with status ${response.status}`);
        }

        const current = this.getData();
        if (current.customerId === this.pageState.customerId && sameIds(idsOf(current.items), idsOf(sent.items))) {
          const stored = new Set(body.products.map(String));
          const items = current.items.filter((item) => stored.has(String(item.id)));
          this.saveData({ items, customerId: this.pageState.customerId, dirty: false, baseIds: idsOf(items) });
          this.refreshUI();
        } else {
          if (current.customerId === this.pageState.customerId) {
            this.saveData(Object.assign({}, current, { baseIds: body.products.map(String) }));
          }
          this.syncQueued = true;
        }
      } catch (error) {
        console.warn(error);
        if (!keepalive) this.showToast('Merkliste konnte nicht gespeichert werden');
      } finally {
        this.syncInFlight = false;
        if (this.syncQueued) {
          this.syncQueued = false;
          this.scheduleSync();
        }
      }
    },

    /**
     * Send a pending debounced sync right away when the page is left
     */
    flushSync: function () {
      if (!this.syncTimer) return;
      clearTimeout(this.syncTimer);
      this.syncTimer = null;
      this.sync(true);
    },

    /**
     * Login URL that returns to the current page.
     * Classic customer accounts: /account/login?return_url=…
     * New customer accounts: /customer_authentication/login?return_to=…
     */
    getLoginUrl: function (baseUrl) {
      const url = new URL(baseUrl || this.pageState.loginUrl, window.location.origin);
      const returnUrl = window.location.pathname + window.location.search;
      if (url.pathname.startsWith('/customer_authentication/')) {
        url.pathname = '/customer_authentication/login';
        url.searchParams.delete('buyer_flags');
        url.searchParams.set('return_to', returnUrl);
      } else {
        url.searchParams.set('return_url', returnUrl);
      }
      return url.pathname + url.search;
    },

    /**
     * Point login links (data-wishlist-login) back to the current page and remember
     * the product to add after login when one is given
     */
    prepareLoginLinks: function (product) {
      document.querySelectorAll('[data-wishlist-login]').forEach((link) => {
        if (!link.dataset.wishlistLoginBase) link.dataset.wishlistLoginBase = link.getAttribute('href');
        link.setAttribute('href', this.getLoginUrl(link.dataset.wishlistLoginBase));
        link.onclick = () => {
          if (product) {
            localStorage.setItem(PENDING_KEY, JSON.stringify({ product, expires: Date.now() + PENDING_TTL }));
          } else {
            localStorage.removeItem(PENDING_KEY);
          }
        };
      });
    },

    /**
     * Show the login dialog for a guest who clicked a wishlist button
     */
    openLoginModal: function (product) {
      const dialog = document.getElementById('wishlist-login-modal');
      if (!dialog || typeof dialog.showModal !== 'function') {
        if (product) {
          localStorage.setItem(PENDING_KEY, JSON.stringify({ product, expires: Date.now() + PENDING_TTL }));
        }
        window.location.href = this.getLoginUrl();
        return;
      }
      this.prepareLoginLinks(product);
      dialog.showModal();
    },

    /**
     * Add the product a guest clicked before logging in
     */
    addPendingProduct: function () {
      if (!this.isLoggedIn()) return;

      const raw = localStorage.getItem(PENDING_KEY);
      if (!raw) return;
      localStorage.removeItem(PENDING_KEY);

      try {
        const pending = safeJsonParse(raw);
        if (!pending || pending.expires < Date.now() || !pending.product) return;
        if (this.isInWishlist(pending.product.id)) return;
        if (this.addProduct(pending.product)) {
          this.showToast(`${pending.product.title} hinzugefügt`);
        }
      } catch (e) {
        console.warn('Wishlist: invalid pending product', e);
      }
    },

    /* ==========================================================================
       5. UI STATE UPDATES
       ========================================================================== */

    /**
     * Bind click and storage events
     */
    bindEvents: function () {
      // Wishlist button clicks (event delegation)
      document.addEventListener('click', (e) => {
        const button = e.target.closest('.wishlist-button');
        if (button) {
          e.preventDefault();
          this.handleButtonClick(button);
        }
      });

      // Sync across browser tabs
      window.addEventListener('storage', (e) => {
        if (e.key === STORAGE_KEY) this.refreshUI();
      });

      // Send a pending change before the page is left
      window.addEventListener('pagehide', () => this.flushSync());
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') this.flushSync();
      });
    },

    /**
     * Update hearts, counters and the wishlist page after the list changed
     */
    refreshUI: function () {
      this.updateButtonStates();
      this.updateCounterBadges();
      this.renderWishlistPage();
    },

    /**
     * Handle wishlist button click with animation
     */
    handleButtonClick: function (button) {
      const productData = {
        id: button.dataset.productId,
        handle: button.dataset.productHandle,
        title: button.dataset.productTitle,
        price: button.dataset.productPrice,
        compare_at_price_min: button.dataset.productCompareAtPriceMin
          ? parseInt(button.dataset.productCompareAtPriceMin, 10)
          : null,
        price_min: button.dataset.productPriceMin ? parseInt(button.dataset.productPriceMin, 10) : null,
        image: button.dataset.productImage,
        url: `/products/${button.dataset.productHandle}`,
      };

      if (!productData.id) return;

      if (!this.isLoggedIn()) {
        this.openLoginModal(productData);
        return;
      }

      const isInWishlist = this.isInWishlist(productData.id);

      // Start animation
      button.classList.add(isInWishlist ? 'wishlist-button--removing' : 'wishlist-button--adding');

      setTimeout(() => {
        // Toggle wishlist state
        if (isInWishlist) {
          this.removeProduct(productData.id);
          this.showToast(`${productData.title} entfernt`);
        } else if (this.addProduct(productData)) {
          this.showToast(`${productData.title} hinzugefügt`);
        }

        // Success animation
        button.classList.remove('wishlist-button--adding', 'wishlist-button--removing');
        button.classList.add('wishlist-button--success');
        setTimeout(() => button.classList.remove('wishlist-button--success'), 600);

        // Update UI
        this.updateButtonStates();
        this.updateCounterBadges();
      }, 200);
    },

    /**
     * Update all wishlist button states on the page
     */
    updateButtonStates: function () {
      document.querySelectorAll('.wishlist-button').forEach((button) => {
        const productId = button.dataset.productId;
        if (!productId) return;

        const isInWishlist = this.isInWishlist(productId);
        button.classList.toggle('wishlist-button--filled', isInWishlist);
        button.classList.toggle('wishlist-button--empty', !isInWishlist);
        button.setAttribute('aria-pressed', isInWishlist);
      });
    },

    /**
     * Update the header count bubbles (sections/header.liquid): replace the loading skeleton with
     * the count, hide them at 0 and put the count into the link label for screen readers
     */
    updateCounterBadges: function () {
      const count = this.getWishlistCount();

      document.querySelectorAll('[data-wishlist-count]').forEach((badge) => {
        badge.textContent = count > 0 ? count : '';
        badge.classList.remove('is-loading');
        badge.classList.toggle('is-visible', count > 0);

        const link = badge.closest('a');
        if (link) link.setAttribute('aria-label', count > 0 ? `Wunschliste (${count})` : 'Wunschliste');
      });
    },

    /* ==========================================================================
       6. TOAST NOTIFICATIONS
       ========================================================================== */

    /**
     * Show toast notification
     * @param {string} message - Message to display (used as headline when bullets are present)
     * @param {Object} [options]
     * @param {string[]} [options.bullets] - Optional bullet list rendered below the message
     */
    showToast: function (message, options) {
      options = options || {};

      // Remove existing toast
      const existing = document.querySelector('.wishlist-toast');
      if (existing) {
        existing.classList.remove('wishlist-toast--visible');
        setTimeout(() => existing.remove(), 300);
      }

      // Create toast element. The bullets path is built with DOM methods +
      // textContent so user-supplied product titles can never escape into HTML
      // — no innerHTML on dynamic values means no escape-bug surface.
      const toast = document.createElement('div');
      toast.className = 'wishlist-toast';

      const content = document.createElement('div');
      content.className = 'wishlist-toast__content';

      if (Array.isArray(options.bullets) && options.bullets.length > 0) {
        const messageEl = document.createElement('div');
        messageEl.className = 'wishlist-toast__message';

        const headline = document.createElement('p');
        headline.className = 'wishlist-toast__headline';
        headline.textContent = message;

        const list = document.createElement('ul');
        list.className = 'wishlist-toast__list';
        options.bullets.forEach((b) => {
          const li = document.createElement('li');
          li.textContent = b;
          list.appendChild(li);
        });

        messageEl.appendChild(headline);
        messageEl.appendChild(list);
        content.appendChild(messageEl);
        toast.appendChild(content);
      } else {
        const messageEl = document.createElement('span');
        messageEl.className = 'wishlist-toast__message';
        messageEl.textContent = message;
        content.appendChild(messageEl);
        toast.appendChild(content);
      }

      // Set variant based on message content
      const lowerMessage = (message || '').toLowerCase();
      if (lowerMessage.includes('hinzugefügt')) {
        toast.classList.add('wishlist-toast--success');
      } else if (lowerMessage.includes('entfernt')) {
        toast.classList.add('wishlist-toast--error');
      } else if (lowerMessage.includes('voll')) {
        toast.classList.add('wishlist-toast--warning');
      }

      document.body.appendChild(toast);

      // Animate in (double rAF for reliable animation)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          toast.classList.add('wishlist-toast--visible');
        });
      });

      // Auto-hide after 3 seconds
      setTimeout(() => {
        toast.classList.remove('wishlist-toast--visible');
        setTimeout(() => toast.remove(), 400);
      }, 3000);
    },

    /* ==========================================================================
       7. WISHLIST PAGE RENDERING
       ========================================================================== */

    /**
     * Format price in cents to currency string
     */
    formatMoney: function (cents) {
      // Use Shopify's Currency formatter if available
      if (
        typeof window.Currency !== 'undefined' &&
        typeof window.Currency.formatMoney === 'function' &&
        typeof window.theme !== 'undefined' &&
        window.theme.moneyFormat
      ) {
        return window.Currency.formatMoney(cents, window.theme.moneyFormat);
      }
      // Fallback: Euro formatting
      return (cents / 100).toFixed(2) + ' €';
    },

    /**
     * Render the wishlist page (called on /pages/wishlist)
     */
    renderWishlistPage: function () {
      const elements = {
        loading: document.getElementById('wishlist-loading'),
        empty: document.getElementById('wishlist-empty'),
        items: document.getElementById('wishlist-items'),
        error: document.getElementById('wishlist-error'),
        counter: document.getElementById('wishlist-counter'),
        clearBtn: document.getElementById('clear-wishlist'),
      };

      if (!elements.items) return;

      const wishlist = this.getWishlist();
      const count = wishlist.length;

      // Hide loading
      if (elements.loading) elements.loading.style.display = 'none';

      // Update counter
      if (elements.counter) elements.counter.textContent = count;

      // Clear button
      if (elements.clearBtn) {
        elements.clearBtn.style.display = count > 0 ? 'block' : 'none';
        elements.clearBtn.onclick = () => {
          if (confirm('Wunschliste wirklich löschen?')) {
            this.clearWishlist();
            this.renderWishlistPage();
            this.updateButtonStates();
            this.updateCounterBadges();
          }
        };
      }

      // Toggle empty/items views
      if (count === 0) {
        if (elements.empty) elements.empty.style.display = 'block';
        if (elements.items) elements.items.style.display = 'none';
        if (elements.error) elements.error.style.display = 'none';
      } else {
        if (elements.empty) elements.empty.style.display = 'none';
        if (elements.error) elements.error.style.display = 'none';
        if (elements.items) {
          elements.items.style.display = 'grid';
          this.renderItems(elements.items, wishlist);
        }
      }
    },

    /**
     * Render wishlist items into container
     * Note: Uses innerHTML with escapeHtml() sanitization to prevent XSS
     */
    renderItems: function (container, items) {
      container.innerHTML = '';

      items.forEach((item) => {
        const itemEl = document.createElement('div');
        itemEl.className = 'wishlist-item';
        itemEl.style.cursor = 'pointer';

        // Sanitize user-controllable values
        const safeTitle = escapeHtml(item.title || '');
        // Only allow trusted image sources (Shopify CDN or relative URLs)
        const safeImage = isValidImageUrl(item.image) ? escapeHtml(item.image) : '';
        // Validate URL - if invalid, reconstruct from handle
        let safeUrl = item.url;
        if (!isValidProductUrl(safeUrl) && item.handle) {
          safeUrl = `/products/${item.handle.toLowerCase().replace(/[^a-z0-9\-_]/g, '')}`;
        }
        safeUrl = isValidProductUrl(safeUrl) ? escapeHtml(safeUrl) : '#';
        const safeId = escapeHtml(String(item.id || ''));

        // Build price HTML
        const priceHTML = this.buildPriceHTML(item);

        // Process title: remove brand prefix (e.g., "Brand | Product" → "Product")
        const displayTitle = safeTitle.includes('|') ? safeTitle.split('|').slice(1).join('|').trim() : safeTitle;

        itemEl.innerHTML = `
          <div class="wishlist-item__image-container">
            ${
              item.image
                ? `<img src="${safeImage}" alt="${safeTitle}" class="wishlist-item__image" loading="lazy">`
                : `<div class="wishlist-item__placeholder">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <rect x="3" y="3" width="18" height="18" rx="2"/>
                      <circle cx="8.5" cy="8.5" r="1.5"/>
                      <polyline points="21,15 16,10 5,21"/>
                    </svg>
                  </div>`
            }
          </div>
          <div class="wishlist-item__content">
            <a href="${safeUrl}" class="wishlist-item__title" onclick="pushEventToDataLayer('wishList:toProductPage', '', window.location.href, '');">${displayTitle}</a>
            <div class="wishlist-item__price">${priceHTML}</div>
            <div class="wishlist-item__actions">
              <a href="${safeUrl}" class="wishlist-item__view-btn" onclick="pushEventToDataLayer('wishList:toProductPage', '', window.location.href, '');">Ansehen</a>
              <button class="wishlist-item__remove-btn" data-product-id="${safeId}">Nicht mehr merken</button>
            </div>
          </div>
        `;

        // Bind event handlers
        this.bindItemEvents(itemEl, item);

        container.appendChild(itemEl);
      });
    },

    /**
     * Build price HTML based on regular/sale prices
     */
    buildPriceHTML: function (item) {
      const regularPrice = item.compare_at_price_min;
      const salePrice = item.price_min;

      if (regularPrice && salePrice && regularPrice > salePrice) {
        return `
          <span class="wishlist-item__regular-price">${this.formatMoney(regularPrice)}</span>
          <span class="wishlist-item__price-separator">·</span>
          <span class="wishlist-item__sale-price">${this.formatMoney(salePrice)}</span>
        `;
      }

      if (salePrice) {
        return `<span class="wishlist-item__sale-price">${this.formatMoney(salePrice)}</span>`;
      }

      return `<span class="wishlist-item__price">${escapeHtml(item.price || '')}</span>`;
    },

    /**
     * Bind click events to wishlist item element
     */
    bindItemEvents: function (itemEl, item) {
      // Validate URL once for all handlers (prevents open redirect)
      const safeUrl = isValidProductUrl(item.url) ? item.url : null;

      // Remove button
      const removeBtn = itemEl.querySelector('.wishlist-item__remove-btn');
      removeBtn.onclick = (e) => {
        e.stopPropagation();
        this.removeProduct(item.id);
        this.showToast(`${item.title} entfernt`);
        this.renderWishlistPage();
        this.updateButtonStates();
        this.updateCounterBadges();
      };

      // Card click (navigate to product)
      itemEl.onclick = (e) => {
        if (
          e.target.closest('.wishlist-item__remove') ||
          e.target.closest('.wishlist-item__view-btn') ||
          e.target.closest('.wishlist-item__title')
        ) {
          return;
        }
        if (safeUrl) {
          window.location.href = safeUrl;
        }
      };

      // Title link
      const titleLink = itemEl.querySelector('.wishlist-item__title');
      titleLink.addEventListener('click', (e) => {
        e.preventDefault();
        if (safeUrl) {
          window.location.href = safeUrl;
        }
      });
    },

    /* ==========================================================================
       8. INITIALIZATION
       ========================================================================== */

    /**
     * Initialize WishlistManager
     */
    init: function () {
      if (!this.checkLocalStorage()) {
        console.warn('LocalStorage not available');
        document.querySelectorAll('[data-wishlist-count]').forEach((badge) => badge.classList.remove('is-loading'));
        return;
      }

      this.pageState = readPageState();
      this.initializeStorage();
      this.reconcileWithAccount();
      this.bindEvents();
      this.prepareLoginLinks();
      this.addPendingProduct();
      this.refreshUI();
    },
  };

  /* ==========================================================================
     EXPORT & AUTO-INIT
     ========================================================================== */

  // Make globally available
  window.WishlistManager = WishlistManager;

  // Auto-initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => WishlistManager.init());
  } else {
    WishlistManager.init();
  }
})();
